/**
 * BookingWidget Component
 *
 * Sticky sidebar booking form with Airbnb-style calendar and price calculation.
 */

import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ListingData } from '../hooks/useListing';
import { useAvailability } from '../hooks/useAvailability';
import { DateRangeCalendar } from '../../../components/calendar';
import { formatDate } from '../../../utils/dates';
import { usePricing } from '../../Booking/hooks/usePricing';
import { useCustomPricing } from '../../Booking/hooks/useCustomPricing';

interface BookingWidgetProps {
  listing: ListingData;
}

export const BookingWidget: React.FC<BookingWidgetProps> = ({ listing }) => {
  const navigate = useNavigate();
  const [checkIn, setCheckIn] = useState<Date | null>(null);
  const [checkOut, setCheckOut] = useState<Date | null>(null);
  const [adults, setAdults] = useState(1);
  const [children, setChildren] = useState(0);
  const [infants, setInfants] = useState(0);
  const [showGuestPicker, setShowGuestPicker] = useState(false);

  const totalGuests = adults + children;
  const [showCalendar, setShowCalendar] = useState(false);
  const calendarRef = useRef<HTMLDivElement>(null);

  const { blockedDateSet } = useAvailability(listing.id);

  // Close calendar on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (calendarRef.current && !calendarRef.current.contains(e.target as Node)) {
        setShowCalendar(false);
      }
    };
    if (showCalendar) {
      document.addEventListener('mousedown', handleClick);
    }
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showCalendar]);

  const customPrices = useCustomPricing(listing.id, checkIn, checkOut);
  const pricing = usePricing(listing.pricing, checkIn, checkOut, customPrices);
  const nights = pricing?.nights ?? 0;

  // Min-nights for this stay: the check-in date's override wins over the
  // listing default (Airbnb-style check-in-date rule).
  const requiredMinNights = checkIn
    ? customPrices?.get(formatDate(checkIn, 'numeric'))?.minNights ??
      listing.minimumStay ?? 1
    : listing.minimumStay ?? 1;
  const minNightsViolated = nights > 0 && nights < requiredMinNights;

  const handleReserve = () => {
    if (!checkIn || !checkOut) {
      setShowCalendar(true);
      return;
    }

    const params = new URLSearchParams({
      checkIn: formatDate(checkIn, 'numeric'),
      checkOut: formatDate(checkOut, 'numeric'),
      adults: String(adults),
      children: String(children),
      infants: String(infants),
    });

    navigate(`/booking/${listing.id}?${params.toString()}`);
  };

  const handleCalendarChange = (newCheckIn: Date | null, newCheckOut: Date | null) => {
    setCheckIn(newCheckIn);
    setCheckOut(newCheckOut);
    if (newCheckIn && newCheckOut) {
      setShowCalendar(false);
    }
  };

  return (
    <div className="sticky top-24 bg-white rounded-xl border border-gray-200 shadow-lg p-6">
      {/* Price */}
      <div className="flex items-baseline gap-1 mb-6">
        <span className="text-2xl font-bold">${listing.pricing.nightlyRate}</span>
        <span className="text-gray-500">/ night</span>
      </div>

      {/* Date Selection - Click to expand calendar */}
      <div className="relative" ref={calendarRef}>
        <button
          type="button"
          onClick={() => setShowCalendar(!showCalendar)}
          className="w-full grid grid-cols-2 border border-gray-300 rounded-lg overflow-hidden mb-4 text-left hover:border-gray-400 transition-colors"
        >
          <div className="p-3 border-r border-gray-300">
            <span className="block text-xs font-semibold text-gray-700 uppercase mb-1">
              Check-in
            </span>
            <span className="text-sm text-gray-900">
              {checkIn ? formatDate(checkIn, 'short') : 'Add date'}
            </span>
          </div>
          <div className="p-3">
            <span className="block text-xs font-semibold text-gray-700 uppercase mb-1">
              Checkout
            </span>
            <span className="text-sm text-gray-900">
              {checkOut ? formatDate(checkOut, 'short') : 'Add date'}
            </span>
          </div>
        </button>

        {/* Calendar Dropdown */}
        {showCalendar && (
          <div className="fixed inset-x-0 bottom-0 md:absolute md:inset-x-auto md:bottom-auto md:top-full md:left-0 md:right-0 mt-1 bg-white rounded-t-xl md:rounded-xl shadow-2xl border border-gray-200 p-4 z-50 md:min-w-[580px] md:-translate-x-1/4 max-h-[80vh] overflow-y-auto">
            <DateRangeCalendar
              checkIn={checkIn}
              checkOut={checkOut}
              onChange={handleCalendarChange}
              blockedDates={blockedDateSet}
              minimumStay={listing.minimumStay || 1}
            />
          </div>
        )}
      </div>

      {/* Guest Selection */}
      <div className="relative mb-4">
        <button
          type="button"
          onClick={() => setShowGuestPicker(!showGuestPicker)}
          className="w-full p-3 border border-gray-300 rounded-lg text-left hover:border-gray-400 transition-colors"
        >
          <label className="block text-xs font-semibold text-gray-700 uppercase mb-1">
            Guests
          </label>
          <span className="text-sm text-gray-900">
            {adults} adult{adults !== 1 ? 's' : ''}
            {children > 0 && `, ${children} child${children !== 1 ? 'ren' : ''}`}
            {infants > 0 && `, ${infants} infant${infants !== 1 ? 's' : ''}`}
          </span>
        </button>

        {showGuestPicker && (
          <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-lg shadow-lg border border-gray-200 p-4 z-10">
            {/* Adults */}
            <div className="flex items-center justify-between py-3">
              <div>
                <span className="text-sm font-medium text-gray-900">Adults</span>
                <p className="text-xs text-gray-500">Age 13+</p>
              </div>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setAdults(Math.max(1, adults - 1))}
                  disabled={adults <= 1}
                  className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center hover:border-gray-400 disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  -
                </button>
                <span className="w-6 text-center text-sm">{adults}</span>
                <button
                  type="button"
                  onClick={() => setAdults(Math.min(listing.maxGuests - children, adults + 1))}
                  disabled={totalGuests >= listing.maxGuests}
                  className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center hover:border-gray-400 disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  +
                </button>
              </div>
            </div>

            {/* Children */}
            <div className="flex items-center justify-between py-3 border-t border-gray-100">
              <div>
                <span className="text-sm font-medium text-gray-900">Children</span>
                <p className="text-xs text-gray-500">Ages 2-12</p>
              </div>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setChildren(Math.max(0, children - 1))}
                  disabled={children <= 0}
                  className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center hover:border-gray-400 disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  -
                </button>
                <span className="w-6 text-center text-sm">{children}</span>
                <button
                  type="button"
                  onClick={() => setChildren(Math.min(listing.maxGuests - adults, children + 1))}
                  disabled={totalGuests >= listing.maxGuests}
                  className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center hover:border-gray-400 disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  +
                </button>
              </div>
            </div>

            {/* Infants */}
            <div className="flex items-center justify-between py-3 border-t border-gray-100">
              <div>
                <span className="text-sm font-medium text-gray-900">Infants</span>
                <p className="text-xs text-gray-500">Under 2</p>
              </div>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setInfants(Math.max(0, infants - 1))}
                  disabled={infants <= 0}
                  className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center hover:border-gray-400 disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  -
                </button>
                <span className="w-6 text-center text-sm">{infants}</span>
                <button
                  type="button"
                  onClick={() => setInfants(Math.min(5, infants + 1))}
                  disabled={infants >= 5}
                  className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center hover:border-gray-400 disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  +
                </button>
              </div>
            </div>

            <p className="text-xs text-gray-500 mt-2">
              Maximum {listing.maxGuests} guests (excluding infants)
            </p>
            <button
              type="button"
              onClick={() => setShowGuestPicker(false)}
              className="w-full mt-3 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 transition-colors"
            >
              Done
            </button>
          </div>
        )}
      </div>

      {/* Reserve Button */}
      <button
        onClick={handleReserve}
        disabled={!checkIn || !checkOut || minNightsViolated}
        className="w-full py-3 bg-primary-500 text-white font-semibold rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {checkIn && checkOut
          ? listing.instantBooking
            ? 'Reserve'
            : 'Request to Book'
          : 'Check availability'}
      </button>

      {!checkIn || !checkOut ? (
        <p className="text-sm text-gray-500 text-center mt-3">
          Select dates to see total price
        </p>
      ) : minNightsViolated ? (
        <p className="text-sm text-red-600 text-center mt-3">
          This stay requires at least {requiredMinNights} night
          {requiredMinNights !== 1 ? 's' : ''}
        </p>
      ) : (
        <p className="text-sm text-gray-500 text-center mt-3">
          You won't be charged yet
        </p>
      )}

      {/* Price Breakdown */}
      {pricing && (
        <div className="mt-6 space-y-3 text-sm">
          <div className="flex justify-between">
            <span className="underline underline-offset-2 text-gray-600">
              ${listing.pricing.nightlyRate} x {nights} night{nights !== 1 ? 's' : ''}
            </span>
            <span>${pricing.nightlyTotal}</span>
          </div>
          {pricing.cleaningFee > 0 && (
            <div className="flex justify-between">
              <span className="underline underline-offset-2 text-gray-600">
                Cleaning fee
              </span>
              <span>${pricing.cleaningFee}</span>
            </div>
          )}
          {pricing.guestServiceFee > 0 && (
            <div className="flex justify-between">
              <span className="underline underline-offset-2 text-gray-600">
                Service fee
              </span>
              <span>${pricing.guestServiceFee}</span>
            </div>
          )}
          <div className="flex justify-between pt-3 border-t border-gray-200 font-semibold">
            <span>Total</span>
            <span>${pricing.totalAmount}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default BookingWidget;
